/** Social compositions built around a capture of the real, public collector-card face. */
export type ShareCardFormat = 'story' | 'portrait' | 'square';
export interface ShareCardStat {
  label: string;
  value: string;
}
export type ShareCardHighlightKind = 'achievement' | 'award' | 'dyno' | 'personal_best';
export interface ShareCardHighlight {
  kind: ShareCardHighlightKind;
  title: string;
  detail?: string | null;
}
export interface ShareCardOptions {
  format: ShareCardFormat;
  /** Share options never alter the public collector face. */
  card: import('../services/api/passportCards').PassportCard;
  title: string;
  subtitle?: string | null;
  ownerName?: string | null;
  stats?: ShareCardStat[];
  highlights?: ShareCardHighlight[] | null;
  /** @deprecated Prefer highlights. */
  highlight?: ShareCardHighlight | null;
  investedText?: string | null;
  /** Public build photo, used only behind the collectible. */
  image?: HTMLImageElement | null;
  shortUrl: string;
  qrImage?: HTMLImageElement | null;
  brandMark?: HTMLImageElement | null;
  accentColor?: string | null;
  dateText?: string | null;
  verifiedLabel?: string | null;
}
export const SHARE_CARD_DIMENSIONS: Record<ShareCardFormat, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  portrait: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};
const WHITE = '#f4f4f1';
const MUTED = '#b8c0c4';
const DISPLAY = 'Outfit, "Work Sans", sans-serif';
const BODY = '"Work Sans", sans-serif';
const capturedFaces = new WeakMap<ShareCardOptions['card'], Promise<HTMLCanvasElement>>();

export function loadImageSafe(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
function textFit(
  ctx: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  maxWidth: number
) {
  let result = value.trim();
  while (result.length > 1 && ctx.measureText(result).width > maxWidth) {
    result = result.slice(0, -2).trimEnd() + '…';
  }
  ctx.fillText(result, x, y);
}
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement | null | undefined,
  height: number
) {
  ctx.fillStyle = '#0b0e10';
  ctx.fillRect(0, 0, 1080, height);
  if (image) {
    const iw = image.naturalWidth || image.width;
    const ih = image.naturalHeight || image.height;
    const scale = Math.max(1176 / iw, (height + 96) / ih);
    const sw = 1176 / scale;
    const sh = (height + 96) / scale;
    ctx.save();
    ctx.filter = 'blur(22px) saturate(.85) brightness(.72)';
    ctx.drawImage(image, (iw - sw) / 2, (ih - sh) / 2, sw, sh, -48, -48, 1176, height + 96);
    ctx.restore();
  }
  const veil = ctx.createLinearGradient(0, 0, 0, height);
  veil.addColorStop(0, 'rgba(8, 11, 13, .72)');
  veil.addColorStop(0.46, 'rgba(8, 11, 13, .38)');
  veil.addColorStop(1, 'rgba(8, 11, 13, .95)');
  ctx.fillStyle = veil;
  ctx.fillRect(0, 0, 1080, height);
  const glow = ctx.createRadialGradient(820, height * 0.24, 10, 820, height * 0.24, 850);
  glow.addColorStop(0, 'rgba(255, 151, 80, .16)');
  glow.addColorStop(1, 'rgba(255, 151, 80, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 1080, height);
}
function drawCard(
  ctx: CanvasRenderingContext2D,
  face: HTMLCanvasElement,
  x: number,
  y: number,
  width: number
) {
  const height = (width * face.height) / face.width;
  ctx.save();
  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate(-0.035);
  // Offset backing echoes the layered card on the homepage.
  ctx.fillStyle = '#171d20';
  ctx.strokeStyle = 'rgba(194, 207, 214, .42)';
  ctx.lineWidth = 2;
  roundRect(ctx, -width / 2 + 16, -height / 2 + 18, width, height, 28);
  ctx.fill();
  ctx.stroke();
  ctx.shadowColor = 'rgba(0, 0, 0, .7)';
  ctx.shadowBlur = 58;
  ctx.shadowOffsetY = 36;
  ctx.drawImage(face, -width / 2, -height / 2, width, height);
  ctx.restore();
}
function drawHeader(
  ctx: CanvasRenderingContext2D,
  options: ShareCardOptions,
  accent: string,
  compact: boolean
) {
  ctx.textAlign = 'left';
  ctx.fillStyle = accent;
  ctx.font = `700 ${compact ? 24 : 26}px ${DISPLAY}`;
  ctx.fillText('BILT:SPEC', 64, compact ? 67 : 80);
  ctx.fillStyle = MUTED;
  ctx.font = `600 ${compact ? 18 : 20}px ${BODY}`;
  ctx.textAlign = 'right';
  ctx.fillText('BUILD / COLLECTIBLE', 1016, compact ? 65 : 78);
  ctx.strokeStyle = 'rgba(221, 231, 235, .3)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(64, compact ? 92 : 108);
  ctx.lineTo(1016, compact ? 92 : 108);
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.fillStyle = WHITE;
  ctx.font = `600 ${compact ? 51 : 58}px ${DISPLAY}`;
  textFit(ctx, options.title, 64, compact ? 162 : 188, 940);
  if (options.subtitle) {
    ctx.fillStyle = MUTED;
    ctx.font = `400 ${compact ? 25 : 28}px ${BODY}`;
    textFit(ctx, options.subtitle, 66, compact ? 201 : 232, 920);
  }
}
function drawStats(
  ctx: CanvasRenderingContext2D,
  stats: ShareCardStat[],
  x: number,
  y: number,
  width: number,
  columns: number,
  accent: string
) {
  if (!stats.length) return y;
  const gap = columns === 4 ? 16 : 18;
  const cellWidth = (width - (columns - 1) * gap) / columns;
  const rowHeight = columns === 4 ? 100 : 104;
  stats.forEach((stat, i) => {
    const cx = x + (i % columns) * (cellWidth + gap);
    const cy = y + Math.floor(i / columns) * (rowHeight + 12);
    ctx.fillStyle = 'rgba(9, 13, 15, .78)';
    ctx.strokeStyle = 'rgba(212, 224, 230, .28)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, cx, cy, cellWidth, rowHeight, 12);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = accent;
    ctx.font = `600 ${columns === 4 ? 27 : 26}px ${DISPLAY}`;
    textFit(ctx, stat.value, cx + 16, cy + 43, cellWidth - 32);
    ctx.fillStyle = MUTED;
    ctx.font = `500 17px ${BODY}`;
    textFit(ctx, stat.label, cx + 16, cy + 74, cellWidth - 32);
  });
  return y + Math.ceil(stats.length / columns) * (rowHeight + 12);
}
function drawHighlights(
  ctx: CanvasRenderingContext2D,
  notes: ShareCardHighlight[],
  x: number,
  y: number,
  width: number,
  rowHeight: number,
  accent: string
) {
  notes.forEach((note, i) => {
    const cy = y + i * rowHeight;
    ctx.fillStyle = accent;
    ctx.fillRect(x, cy + 4, 3, rowHeight - 17);
    ctx.fillStyle = WHITE;
    ctx.font = `600 ${rowHeight < 80 ? 22 : 25}px ${DISPLAY}`;
    textFit(ctx, note.title, x + 20, cy + 30, width - 22);
    if (note.detail) {
      ctx.fillStyle = MUTED;
      ctx.font = `400 ${rowHeight < 80 ? 17 : 19}px ${BODY}`;
      textFit(ctx, note.detail, x + 20, cy + 57, width - 22);
    }
    ctx.strokeStyle = 'rgba(218, 229, 234, .20)';
    ctx.beginPath();
    ctx.moveTo(x + 20, cy + rowHeight - 5);
    ctx.lineTo(x + width, cy + rowHeight - 5);
    ctx.stroke();
  });
}
function drawFooter(
  ctx: CanvasRenderingContext2D,
  options: ShareCardOptions,
  height: number,
  accent: string
) {
  const y = height - 91;
  ctx.strokeStyle = 'rgba(221, 231, 235, .3)';
  ctx.beginPath();
  ctx.moveTo(64, y - 27);
  ctx.lineTo(1016, y - 27);
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.fillStyle = accent;
  ctx.font = `600 23px ${BODY}`;
  textFit(ctx, options.shortUrl, 64, y + 12, options.qrImage ? 780 : 940);
  if (options.dateText) {
    ctx.fillStyle = MUTED;
    ctx.font = `400 18px ${BODY}`;
    textFit(ctx, options.dateText, 64, y + 44, 700);
  }
  if (options.qrImage) {
    ctx.fillStyle = WHITE;
    roundRect(ctx, 924, height - 132, 92, 92, 8);
    ctx.fill();
    ctx.drawImage(options.qrImage, 932, height - 124, 76, 76);
  }
}
export async function renderShareCard(options: ShareCardOptions): Promise<Blob> {
  let facePromise = capturedFaces.get(options.card);
  if (!facePromise) {
    let deadline: number;
    facePromise = Promise.race([
      import('../components/passport/captureCollectorCard').then((module) =>
        module.captureCollectorCard(options.card, false, options.image)
      ),
      new Promise<HTMLCanvasElement>((_, reject) => {
        deadline = window.setTimeout(
          () => reject(new Error('Preview took too long to render. Please try again.')),
          45000
        );
      }),
    ]).finally(() => window.clearTimeout(deadline));
    capturedFaces.set(options.card, facePromise);
    void facePromise.catch(() => capturedFaces.delete(options.card));
  }
  const face = await facePromise;
  const { width, height } = SHARE_CARD_DIMENSIONS[options.format];
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Image export is unavailable');
  const accent = options.accentColor || '#ff9750';
  const notes = (options.highlights || (options.highlight ? [options.highlight] : [])).slice(0, 3);
  const stats = (options.stats || []).slice(0, 4);
  const hasExtras =
    stats.length > 0 ||
    notes.length > 0 ||
    !!options.investedText ||
    !!options.verifiedLabel ||
    !!options.ownerName;
  drawBackdrop(ctx, options.image, height);
  drawHeader(ctx, options, accent, options.format === 'square');
  if (options.format === 'story') {
    drawCard(ctx, face, hasExtras ? 203 : 120, hasExtras ? 274 : 260, hasExtras ? 674 : 840);
    let y = hasExtras ? 1308 : 1420;
    if (options.ownerName || options.verifiedLabel) {
      ctx.fillStyle = MUTED;
      ctx.font = `500 23px ${BODY}`;
      textFit(
        ctx,
        [options.ownerName ? `Built by ${options.ownerName}` : '', options.verifiedLabel]
          .filter(Boolean)
          .join('  ·  '),
        70,
        y - 18,
        930
      );
    }
    if (stats.length) y = drawStats(ctx, stats, 70, y, 940, 4, accent) + 21;
    drawHighlights(ctx, notes, 72, y, 934, 91, accent);
  } else {
    const square = options.format === 'square';
    const cardWidth = hasExtras ? (square ? 447 : 532) : square ? 500 : 610;
    drawCard(
      ctx,
      face,
      hasExtras ? (square ? 66 : 63) : (width - cardWidth) / 2,
      square ? (hasExtras ? 256 : 215) : hasExtras ? 310 : 284,
      cardWidth
    );
    if (hasExtras || options.ownerName) {
      const x = square ? 566 : 646;
      const panelWidth = square ? 450 : 370;
      const panelY = square ? 253 : 313;
      const panelBottom = height - 154;
      ctx.fillStyle = 'rgba(8, 12, 14, .73)';
      ctx.strokeStyle = 'rgba(215, 228, 235, .25)';
      roundRect(ctx, x - 16, panelY - 16, panelWidth + 32, panelBottom - panelY + 32, 18);
      ctx.fill();
      ctx.stroke();
      let y = panelY + 16;
      if (options.ownerName) {
        ctx.fillStyle = accent;
        ctx.font = `600 22px ${BODY}`;
        textFit(ctx, `Built by ${options.ownerName}`, x, y + 7, panelWidth);
        y += 48;
      }
      if (options.verifiedLabel) {
        ctx.fillStyle = MUTED;
        ctx.font = `600 17px ${BODY}`;
        textFit(ctx, options.verifiedLabel.toUpperCase(), x, y + 3, panelWidth);
        y += 36;
      }
      if (stats.length) y = drawStats(ctx, stats, x, y, panelWidth, 2, accent) + 15;
      if (options.investedText) {
        ctx.fillStyle = MUTED;
        ctx.font = `500 19px ${BODY}`;
        textFit(ctx, `${options.investedText} invested`, x, y + 9, panelWidth);
        y += 48;
      }
      drawHighlights(ctx, notes, x, y, panelWidth, square ? 76 : 87, accent);
    }
  }
  drawFooter(ctx, options, height, accent);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image export failed'))),
      'image/png'
    )
  );
}
